import Service from '@ember/service';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import config from '@linc-technologies/linced-common/utils/config';
import { LogLevel } from '@grafana/faro-web-sdk';

export default class LoggingService extends Service {
  @service
  faro;

  // User context is read from sessionStorage (written by session service)
  // rather than injecting session directly. This avoids a circular
  // dependency: session.init → logging.pushLog → session (injects session)
  // → session.init → ...
  addCommonContext(context = {}) {
    return {
      ...context,
      userId: sessionStorage.getItem('logging:userId'),
      tabId: sessionStorage.getItem('tabId'),
      scopes: JSON.parse(sessionStorage.getItem('logging:scopes') || '[]'),
    };
  }

  buildLogLine(value, funcName = '', context = {}) {
    let logLine = `function: ${funcName} | ${value}`;
    if (!isEmpty(context.package)) {
      logLine = `${context.package} | ${logLine}`;
    }
    logLine = `${new Date().toISOString()} | ${logLine}`;
    if (!isEmpty(context.requestUri)) {
      logLine = `${logLine} | uri: ${context.requestUri}`;
    }
    return logLine;
  }

  buildContext(funcName = '', context = {}, fallbackLevel) {
    const builtContext = {
      ...context,
      level: isEmpty(context.level) ? fallbackLevel : context.level,
    };
    if (funcName) {
      builtContext.function = funcName;
    }
    if (isEmpty(builtContext.package)) {
      builtContext.package = 'ember';
    }
    return this.addCommonContext(builtContext);
  }

  pushWithContext(value, funcName = '', context = {}, options = {}) {
    const {
      fallbackLevel,
      logToConsole,
      sendToFaro,
      transformValue = (rawValue) => rawValue,
    } = options;
    context = this.buildContext(funcName, context, fallbackLevel);
    const logLine = this.buildLogLine(value, funcName, context);
    if (config.environment === 'development') {
      logToConsole(logLine);
    }

    const faroOptions = { context: context };
    sendToFaro(transformValue(value), faroOptions);
  }

  pushError(value, funcName = '', context = {}) {
    this.pushWithContext(value, funcName, context, {
      fallbackLevel: LogLevel.ERROR,
      logToConsole: (line) => console.error(line),
      sendToFaro: (payload, options) => this.faro.pushError(payload, options),
    });
  }

  pushLog(value, funcName = '', context = {}) {
    this.pushWithContext(value, funcName, context, {
      fallbackLevel: LogLevel.LOG,
      logToConsole: (line) => console.log(line),
      sendToFaro: (payload, options) => this.faro.pushLog(payload, options),
    });
  }

  pushEvent(name) {
    const now = new Date();
    if (config.environment === 'development') {
      console.debug(`${now.toISOString()} | event: ${name}`);
    }
    const userId = sessionStorage.getItem('logging:userId');
    this.faro.pushEvent(name, userId ? { userId } : undefined);
  }
}
